import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { logger } from '@/lib/logger';

export type CookieCategory = 'essential' | 'analytics' | 'marketing' | 'comfort';

export interface CookieConsent {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  comfort: boolean;
  timestamp?: number;
  version?: string;
}

type ConsentChangeCallback = (consent: CookieConsent) => void;

interface CookieConsentContextType {
  consent: CookieConsent;
  showBanner: boolean;
  updateConsent: (newConsent: Partial<CookieConsent>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  hideBanner: () => void;
  resetConsent: () => void;
  onConsentChange: (callback: ConsentChangeCallback) => () => void;
}

const defaultConsent: CookieConsent = {
  essential: true, // Immer aktiv nach DSGVO
  analytics: false,
  marketing: false,
  comfort: false,
  timestamp: Date.now(),
  version: '1.0'
};

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({ children }) => {
  const [consent, setConsent] = useState<CookieConsent>(defaultConsent);
  const [showBanner, setShowBanner] = useState(false);
  const consentCallbacks = React.useRef<Set<ConsentChangeCallback>>(new Set());

  useEffect(() => {
    // Laden des gespeicherten Consent-Status
    const savedConsent = localStorage.getItem('cookie-consent');
    if (savedConsent) {
      try {
        const parsedConsent = JSON.parse(savedConsent);
        setConsent(parsedConsent);
        setShowBanner(false);
      } catch (error) {
        logger.error('Failed to load cookie settings', 'COOKIES', { 
          error: error instanceof Error ? error.message : String(error) 
        });
        setShowBanner(true);
      }
    } else {
      // Wenn noch kein Consent gespeichert ist, Banner anzeigen
      setShowBanner(true);
    }
  }, []);

  const saveConsent = (newConsent: CookieConsent) => {
    const consentWithTimestamp = {
      ...newConsent,
      timestamp: Date.now(),
      version: '1.0'
    };
    localStorage.setItem('cookie-consent', JSON.stringify(consentWithTimestamp));
    setConsent(consentWithTimestamp);
    setShowBanner(false);
    
    // Notify all registered callbacks
    consentCallbacks.current.forEach(callback => {
      try {
        callback(consentWithTimestamp);
      } catch (error) {
        logger.error('Consent callback error', 'COOKIES', { error });
      }
    });
  };

  const onConsentChange = React.useCallback((callback: ConsentChangeCallback) => {
    consentCallbacks.current.add(callback);
    return () => {
      consentCallbacks.current.delete(callback);
    };
  }, []);

  const updateConsent = (newConsent: Partial<CookieConsent>) => {
    const updatedConsent = { ...consent, ...newConsent };
    saveConsent(updatedConsent);
  };

  const acceptAll = () => {
    saveConsent({
      essential: true,
      analytics: true,
      marketing: true,
      comfort: true
    });
  };

  const rejectAll = () => {
    saveConsent({
      essential: true, // Essentiell immer aktiv
      analytics: false,
      marketing: false,
      comfort: false
    });
  };

  const hideBanner = () => {
    setShowBanner(false);
  };

  const resetConsent = () => {
    localStorage.removeItem('cookie-consent');
    setConsent(defaultConsent);
    setShowBanner(true);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        showBanner,
        updateConsent,
        acceptAll,
        rejectAll,
        hideBanner,
        resetConsent,
        onConsentChange
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};